import {CommonModule} from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {finalize} from 'rxjs';
import {
  ChatContactView,
  ChatCurrentUserView,
  ChatMessageView,
  ChatUserSearchItem
} from '../../../models/generated-models';
import {ChatRestService} from './chat-rest.service';
import {ToastService} from '../../shared/commons/toast/toast-service';

interface ChatAttachmentDraft {
  file: File;
  name: string;
  type: 'image' | 'file';
  previewUrl: string | null;
}

@Component({
  selector: 'app-chat',
  imports: [CommonModule, FormsModule],
  templateUrl: './chat.html',
  styleUrl: './chat.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Chat implements OnInit, AfterViewInit, OnDestroy {
  private static readonly MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024;
  private static readonly MESSAGE_PAGE_SIZE = 10;
  private readonly chatRest = inject(ChatRestService);
  private readonly toast = inject(ToastService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly route = inject(ActivatedRoute);

  @ViewChild('messagePanel') messagePanel?: ElementRef<HTMLDivElement>;
  @ViewChild('composerTextarea') composerTextarea?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('attachmentInput') attachmentInput?: ElementRef<HTMLInputElement>;

  protected currentUser?: ChatCurrentUserView;
  protected contacts: ChatContactView[] = [];
  protected searchResults: ChatUserSearchItem[] = [];
  protected selectedContact?: ChatContactView;
  protected messages: ChatMessageView[] = [];
  protected searchQuery = '';
  protected draftMessage = '';
  protected attachmentFiles: ChatAttachmentDraft[] = [];
  protected attachmentDragging = false;
  protected loadingContacts = false;
  protected loadingMessages = false;
  protected loadingOlderMessages = false;
  protected loadingSearch = false;
  protected updatingAllowChat = false;
  protected updatingAllowSearch = false;
  protected sending = false;
  protected unreadCount = 0;
  protected searchExpanded = true;
  protected contactsExpanded = true;
  protected imageViewerOpen = false;
  protected imageViewerLoading = false;
  protected imageViewerSrc = '';
  protected imageViewerAlt = '';
  protected imageViewerIndex = -1;

  private pollHandle?: number;
  private viewReady = false;
  private pendingScrollToBottom = false;
  private pendingScrollPreserve: {scrollHeight: number; scrollTop: number} | null = null;
  private activeConversationContactId: number | null = null;
  private oldestLoadedMessageId: number | null = null;
  private newestLoadedMessageId: number | null = null;
  private conversationHasMoreOlder = true;
  private restoreComposerFocus = false;
  private autoLoadOlderHistory = false;

  ngOnInit(): void {
    this.loadMe();
    this.refreshContacts();
    this.openConversationFromQueryParam();
    this.pollHandle = window.setInterval(() => {
      this.refreshContacts(false, false);
      if (this.selectedContact) {
        this.refreshMessages(this.selectedContact.contactId, false, false);
      }
      this.refreshUnreadCount(false);
    }, 5000);
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.scrollToBottom();
  }

  ngAfterViewChecked(): void {
    if (!this.viewReady) {
      return;
    }
    const el = this.messagePanel?.nativeElement;
    if (!el) {
      return;
    }
    if (this.pendingScrollPreserve) {
      const {scrollHeight, scrollTop} = this.pendingScrollPreserve;
      el.scrollTop = el.scrollHeight - scrollHeight + scrollTop;
      this.pendingScrollPreserve = null;
      return;
    }
    if (this.pendingScrollToBottom) {
      el.scrollTop = el.scrollHeight;
      this.pendingScrollToBottom = false;
    }
    if (this.restoreComposerFocus) {
      this.restoreComposerFocus = false;
      window.requestAnimationFrame(() => {
        const textarea = this.composerTextarea?.nativeElement;
        if (!textarea || textarea.disabled) {
          return;
        }
        textarea.focus({preventScroll: true});
        const length = textarea.value.length;
        textarea.setSelectionRange(length, length);
      });
    }
  }

  ngOnDestroy(): void {
    if (this.pollHandle) {
      window.clearInterval(this.pollHandle);
    }
  }

  loadMe(): void {
    this.chatRest.me().subscribe({
      next: (user) => {
        this.currentUser = user;
        this.requestCheck();
      },
      error: () => {
        this.requestCheck();
      }
    });
  }

  protected get allowChatEnabled(): boolean {
    return this.currentUser?.allowChat === true;
  }

  protected get allowSearchEnabled(): boolean {
    return this.currentUser?.allowSearchByName === true;
  }

  toggleAllowChat(): void {
    if (this.updatingAllowChat) {
      return;
    }
    this.updatingAllowChat = true;
    this.chatRest.updateAllowChat(!this.allowChatEnabled).subscribe({
      next: (user) => {
        this.currentUser = user;
        this.updatingAllowChat = false;
        this.toast.success(this.allowChatEnabled ? 'เปิดการค้นหาผู้ติดต่อแชทแล้ว' : 'ปิดการค้นหาผู้ติดต่อแชทแล้ว');
        this.requestCheck();
      },
      error: () => {
        this.updatingAllowChat = false;
        this.requestCheck();
      }
    });
  }

  toggleAllowSearch(): void {
    if (this.updatingAllowSearch) {
      return;
    }
    this.updatingAllowSearch = true;
    this.chatRest.updateAllowSearch(!this.allowSearchEnabled).subscribe({
      next: (user) => {
        this.currentUser = user;
        this.updatingAllowSearch = false;
        this.toast.success(this.allowSearchEnabled ? 'เปิดการค้นหาผู้ติดต่อด้วยชื่อ-สกุลแล้ว' : 'ปิดการค้นหาผู้ติดต่อด้วยชื่อ-สกุลแล้ว');
        this.requestCheck();
      },
      error: () => {
        this.updatingAllowSearch = false;
        this.requestCheck();
      }
    });
  }

  refreshContacts(showToast = false, showLoading = true): void {
    if (showLoading) {
      this.loadingContacts = true;
    }
    this.chatRest.contacts().pipe(
      finalize(() => {
        this.loadingContacts = false;
      })
    ).subscribe({
      next: (contacts) => {
        this.contacts = this.sortContactsByRecentActivity(contacts);
      if (!this.selectedContact && this.contacts.length > 0 && window.innerWidth > 991) {
        this.selectContact(this.contacts[0]);
      } else if (this.selectedContact) {
          const refreshed = this.contacts.find(contact => contact.contactId === this.selectedContact?.contactId);
          this.selectedContact = refreshed ?? this.selectedContact;
        }
        this.requestCheck();
      },
      error: () => {
        this.requestCheck();
      }
    });
  }

  refreshUnreadCount(showToast = false): void {
    this.chatRest.unreadCount().subscribe({
      next: (count) => {
        this.unreadCount = count;
        this.requestCheck();
      },
      error: () => {
        this.requestCheck();
      }
    });
  }

  searchUsers(): void {
    const query = this.searchQuery.trim();
    if (!query) {
      this.searchResults = [];
      return;
    }
    this.loadingSearch = true;
    this.chatRest.searchUsers(query).subscribe({
      next: (users) => {
        this.searchResults = users;
        this.loadingSearch = false;
        this.requestCheck();
      },
      error: () => {
        this.loadingSearch = false;
        this.requestCheck();
      }
    });
  }

  /**
   * Deep-link entry: when another page navigates to /chat?contactId=<id> (e.g. the
   * "chat with this person" shortcut on a purchase chain node), open that
   * conversation directly. addContact is idempotent and re-enforces the target's
   * allow-chat opt-in server-side, so this is safe even if the param is stale.
   */
  private openConversationFromQueryParam(): void {
    const raw = this.route.snapshot.queryParamMap.get('contactId');
    if (!raw) {
      return;
    }
    const contactId = Number(raw);
    if (!Number.isFinite(contactId) || contactId <= 0) {
      return;
    }
    this.chatRest.addContact(contactId).subscribe({
      next: (contact) => {
        this.refreshContacts();
        this.selectContact(contact);
        this.requestCheck();
      },
      error: () => {
        this.requestCheck();
      }
    });
  }

  addContact(user: ChatUserSearchItem): void {
    this.chatRest.addContact(user.id).subscribe({
      next: (contact) => {
        this.toast.success(`เพิ่ม ${contact.displayName} เป็นผู้ติดต่อแล้ว`);
        this.searchResults = [];
        this.searchQuery = '';
        this.refreshContacts();
        this.selectContact(contact);
        this.requestCheck();
      },
      error: () => {
        this.requestCheck();
      }
    });
  }

  selectContact(contact: ChatContactView): void {
    this.selectedContact = contact;
    this.autoLoadOlderHistory = false;
    if (this.activeConversationContactId === contact.contactId && this.messages.length > 0) {
      this.refreshMessages(contact.contactId, false, false);
    } else {
      this.loadInitialMessages(contact.contactId);
    }
    this.requestCheck();
  }

  toggleContactsExpanded(): void {
    this.contactsExpanded = !this.contactsExpanded;
    this.requestCheck();
  }

  toggleSearchExpanded(): void {
    this.searchExpanded = !this.searchExpanded;
    this.requestCheck();
  }

  protected backToContactList(): void {
    this.selectedContact = undefined;
    this.messages = [];
    this.draftMessage = '';
    this.clearAttachments();
    this.activeConversationContactId = null;
    this.oldestLoadedMessageId = null;
    this.newestLoadedMessageId = null;
    this.requestCheck();
  }

  refreshMessages(contactId: number, showToast = false, showLoading = true): void {
    if (this.loadingOlderMessages) {
      return;
    }
    if (this.activeConversationContactId !== contactId || this.messages.length === 0) {
      this.loadInitialMessages(contactId, showToast, showLoading);
      return;
    }
    if (this.newestLoadedMessageId == null) {
      this.loadInitialMessages(contactId, showToast, showLoading);
      return;
    }
    this.loadNewerMessages(contactId, showToast, showLoading);
  }

  onMessagePanelScroll(): void {
    const el = this.messagePanel?.nativeElement;
    if (!el || this.loadingMessages || this.loadingOlderMessages || !this.conversationHasMoreOlder) {
      if (el && el.scrollTop > 40) {
        this.autoLoadOlderHistory = false;
      }
      return;
    }
    if (el.scrollTop > 40) {
      this.autoLoadOlderHistory = false;
      return;
    }
    this.autoLoadOlderHistory = true;
    this.loadOlderMessages();
  }

  private loadInitialMessages(contactId: number, showToast = false, showLoading = true): void {
    if (showLoading) {
      this.loadingMessages = true;
    }
    this.loadingOlderMessages = false;
    this.autoLoadOlderHistory = false;
    this.messages = [];
    this.activeConversationContactId = contactId;
    this.oldestLoadedMessageId = null;
    this.newestLoadedMessageId = null;
    this.conversationHasMoreOlder = true;
    this.chatRest.messages(contactId, {limit: Chat.MESSAGE_PAGE_SIZE}).subscribe({
      next: (messages) => {
        this.messages = messages;
        this.loadingMessages = false;
        this.activeConversationContactId = contactId;
        this.oldestLoadedMessageId = messages[0]?.id ?? null;
        this.newestLoadedMessageId = messages[messages.length - 1]?.id ?? null;
        this.conversationHasMoreOlder = messages.length === Chat.MESSAGE_PAGE_SIZE;
        this.scrollToBottom();
        this.refreshContacts(false, false);
        this.refreshUnreadCount();
        this.requestCheck();
      },
      error: () => {
        this.loadingMessages = false;
        this.requestCheck();
      }
    });
  }

  private loadNewerMessages(contactId: number, showToast = false, showLoading = true): void {
    if (showLoading) {
      this.loadingMessages = true;
    }
    this.chatRest.messages(contactId, {
      limit: Chat.MESSAGE_PAGE_SIZE,
      afterMessageId: this.newestLoadedMessageId
    }).subscribe({
      next: (messages) => {
        const shouldStickToBottom = this.isMessagePanelNearBottom();
        if (messages.length > 0) {
          this.messages = [...this.messages, ...messages];
          this.newestLoadedMessageId = messages[messages.length - 1]?.id ?? this.newestLoadedMessageId;
        }
        this.loadingMessages = false;
        if (shouldStickToBottom) {
          this.scrollToBottom();
        }
        this.refreshContacts(false, false);
        this.refreshUnreadCount();
        this.requestCheck();
      },
      error: () => {
        this.loadingMessages = false;
        this.requestCheck();
      }
      });
  }

  private loadOlderMessages(): void {
    if (this.loadingMessages || this.loadingOlderMessages || !this.activeConversationContactId || this.oldestLoadedMessageId == null) {
      return;
    }
    this.loadingOlderMessages = true;
    this.pendingScrollPreserve = this.captureMessagePanelPosition();
    this.chatRest.messages(this.activeConversationContactId, {
      limit: Chat.MESSAGE_PAGE_SIZE,
      beforeMessageId: this.oldestLoadedMessageId
    }).subscribe({
      next: (messages) => {
        if (messages.length > 0) {
          this.messages = [...messages, ...this.messages];
          this.oldestLoadedMessageId = messages[0]?.id ?? this.oldestLoadedMessageId;
          this.conversationHasMoreOlder = messages.length === Chat.MESSAGE_PAGE_SIZE;
        } else {
          this.conversationHasMoreOlder = false;
        }
        this.loadingOlderMessages = false;
        this.refreshContacts(false, false);
        this.requestCheck();
        if (this.autoLoadOlderHistory && this.conversationHasMoreOlder) {
          window.requestAnimationFrame(() => {
            if (this.autoLoadOlderHistory && this.conversationHasMoreOlder && !this.loadingOlderMessages) {
              this.loadOlderMessages();
            }
          });
        }
      },
      error: () => {
        this.loadingOlderMessages = false;
        this.pendingScrollPreserve = null;
        this.autoLoadOlderHistory = false;
        this.requestCheck();
      }
    });
  }

  sendMessage(): void {
    const text = this.draftMessage.trim();
    if (!this.selectedContact || this.sending || (!text && !this.hasAttachments())) {
      return;
    }
    this.sending = true;
    if (this.hasAttachments()) {
      const files = this.attachmentFiles.map(item => item.file);
      this.chatRest.sendAttachments(this.selectedContact.contactId, files).subscribe({
        next: (messages) => {
          this.messages = [...this.messages, ...messages];
          this.newestLoadedMessageId = messages[messages.length - 1]?.id ?? this.newestLoadedMessageId;
          this.activeConversationContactId = this.selectedContact?.contactId ?? this.activeConversationContactId;
          this.clearAttachments();
          this.draftMessage = '';
          this.sending = false;
          this.focusComposer();
          this.refreshContacts(false, false);
          this.refreshUnreadCount();
          this.scrollToBottom();
          this.requestCheck();
        },
        error: () => {
          this.sending = false;
          this.focusComposer();
          this.requestCheck();
        }
      });
      return;
    }
    this.chatRest.sendMessage({
      receiverId: this.selectedContact.contactId,
      message: text,
      messageType: 'text'
    }).subscribe({
      next: (message) => {
        this.messages = [...this.messages, message];
        this.newestLoadedMessageId = message.id ?? this.newestLoadedMessageId;
        this.activeConversationContactId = this.selectedContact?.contactId ?? this.activeConversationContactId;
        this.draftMessage = '';
        this.sending = false;
        this.focusComposer();
        this.refreshContacts(false, false);
        this.refreshUnreadCount();
        this.scrollToBottom();
        this.requestCheck();
      },
      error: () => {
        this.sending = false;
        this.focusComposer();
        this.requestCheck();
      }
    });
  }

  openAttachmentPicker(): void {
    this.attachmentInput?.nativeElement.click();
  }

  onAttachmentSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    this.applyAttachments(files);
  }

  onComposerDragOver(event: DragEvent): void {
    event.preventDefault();
    this.attachmentDragging = true;
    this.requestCheck();
  }

  onComposerDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.attachmentDragging = false;
    this.requestCheck();
  }

  onComposerDrop(event: DragEvent): void {
    event.preventDefault();
    this.attachmentDragging = false;
    const files = Array.from(event.dataTransfer?.files ?? []);
    this.applyAttachments(files);
  }

  removeAttachment(): void {
    this.clearAttachments();
    this.requestCheck();
  }

  removeAttachmentAt(index: number): void {
    if (index < 0 || index >= this.attachmentFiles.length) {
      return;
    }
    const [removed] = this.attachmentFiles.splice(index, 1);
    if (removed?.previewUrl) {
      URL.revokeObjectURL(removed.previewUrl);
    }
    if (this.attachmentFiles.length === 0) {
      this.clearAttachments();
    }
    this.requestCheck();
  }

  onComposerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  formatTime(value: string | Date | null | undefined): string {
    if (!value) {
      return '';
    }
    return new Intl.DateTimeFormat('th-TH', {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: 'short'
    }).format(value instanceof Date ? value : new Date(value));
  }

  initials(name: string | null | undefined): string {
    if (!name) {
      return '?';
    }
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase();
  }

  contactLabel(contact: Pick<ChatContactView, 'displayName' | 'email' | 'contactId'>): string {
    return contact.displayName?.trim()
      || contact.email?.trim()
      || `ผู้ติดต่อ #${contact.contactId}`;
  }

  isContactOnline(contact: Pick<ChatContactView, 'online'>): boolean {
    return !!contact.online;
  }

  contactPresenceClass(contact: Pick<ChatContactView, 'online'>): string {
    return this.isContactOnline(contact) ? 'online' : 'offline';
  }

  userLabel(user: Pick<ChatUserSearchItem, 'displayName' | 'email' | 'id'>): string {
    return user.displayName?.trim()
      || user.email?.trim()
      || `ผู้ใช้ #${user.id}`;
  }

  attachmentDisplayLabel(): string {
    if (!this.attachmentFiles.length) {
      return '';
    }
    if (this.attachmentFiles.length === 1) {
      return this.attachmentFiles[0].name;
    }
    return `${this.attachmentFiles.length} ไฟล์`;
  }

  attachmentIconClass(): string {
    return 'bi-paperclip';
  }

  messageAttachmentName(value: string): string {
    try {
      const url = new URL(value, window.location.origin);
      const parts = url.pathname.split('/').filter(Boolean);
      return decodeURIComponent(parts[parts.length - 1] ?? value);
    } catch {
      const parts = value.split('/').filter(Boolean);
      return decodeURIComponent(parts[parts.length - 1] ?? value);
    }
  }

  isImageMessage(message: ChatMessageView): boolean {
    return message.messageType === 'image';
  }

  openImageModal(message: ChatMessageView): void {
    if (!this.isImageMessage(message) || !message.messageData) {
      return;
    }

    const index = this.imageMessages.findIndex(item => item.id === message.id);
    if (index < 0) {
      return;
    }

    this.imageViewerIndex = index;
    this.imageViewerOpen = true;
    this.imageViewerLoading = true;
    this.imageViewerSrc = '';
    this.imageViewerAlt = this.messageAttachmentName(message.messageData);
    this.requestCheck();

    this.loadImageAt(index);
  }

  closeImageModal(): void {
    this.imageViewerOpen = false;
    this.imageViewerLoading = false;
    this.imageViewerSrc = '';
    this.imageViewerAlt = '';
    this.imageViewerIndex = -1;
    this.requestCheck();
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.imageViewerOpen) {
      this.closeImageModal();
    }
  }

  @HostListener('document:keydown', ['$event'])
  onImageViewerKeydown(event: KeyboardEvent): void {
    if (!this.imageViewerOpen) {
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.showPreviousImage();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.showNextImage();
    }
  }

  showPreviousImage(): void {
    if (!this.hasPreviousImage) {
      return;
    }
    this.openImageAt(this.imageViewerIndex - 1);
  }

  showNextImage(): void {
    if (!this.hasNextImage) {
      return;
    }
    this.openImageAt(this.imageViewerIndex + 1);
  }

  isFileMessage(message: ChatMessageView): boolean {
    return message.messageType === 'file';
  }

  private requestCheck(): void {
    this.cdr.markForCheck();
  }

  protected get imageMessages(): ChatMessageView[] {
    return this.messages.filter(message => this.isImageMessage(message) && !!message.messageData);
  }

  protected get hasPreviousImage(): boolean {
    return this.imageViewerIndex > 0;
  }

  protected get hasNextImage(): boolean {
    return this.imageViewerIndex >= 0 && this.imageViewerIndex < this.imageMessages.length - 1;
  }

  protected hasAttachments(): boolean {
    return this.attachmentFiles.length > 0;
  }

  protected attachmentCountLabel(): string {
    return this.attachmentFiles.length === 1 ? '1 ไฟล์' : `${this.attachmentFiles.length} ไฟล์`;
  }

  protected attachmentPreviewItems(): ChatAttachmentDraft[] {
    return this.attachmentFiles;
  }

  protected attachmentRemoveLabel(index: number): string {
    const item = this.attachmentFiles[index];
    return item ? `ลบ ${item.name}` : 'ลบไฟล์';
  }

  protected attachmentKindLabel(item: ChatAttachmentDraft): string {
    return item.type === 'image' ? 'รูปภาพ' : 'ไฟล์';
  }

  private applyAttachments(files: File[]): void {
    if (!files.length) {
      this.clearAttachments();
      this.requestCheck();
      return;
    }

    const validFiles = files.filter(file => {
      if (file.size > Chat.MAX_ATTACHMENT_SIZE) {
        this.toast.error(`ไฟล์ ${file.name} ใหญ่เกิน 10 MB`);
        return false;
      }
      return true;
    });

    if (!validFiles.length) {
      this.clearAttachments();
      this.requestCheck();
      return;
    }

    this.clearAttachments();
    this.attachmentFiles = validFiles.map(file => ({
      file,
      name: file.name,
      type: file.type.startsWith('image/') ? 'image' : 'file',
      previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    }));
    this.draftMessage = '';
    this.requestCheck();
  }

  private clearAttachments(): void {
    this.attachmentFiles.forEach(item => {
      if (item.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
      }
    });
    this.attachmentFiles = [];
    if (this.attachmentInput?.nativeElement) {
      this.attachmentInput.nativeElement.value = '';
    }
  }

  trackById(_: number, item: {id?: number; contactId?: number}): number {
    return item.contactId ?? item.id ?? 0;
  }

  trackByContactId(_: number, item: ChatContactView): number {
    return item.contactId;
  }

  trackByMessageId(_: number, item: ChatMessageView): number {
    return item.id;
  }

  trackByAttachmentIndex(index: number): number {
    return index;
  }

  private scrollToBottom(): void {
    this.pendingScrollToBottom = true;
  }

  private focusComposer(): void {
    this.restoreComposerFocus = true;
    this.requestCheck();
  }

  private captureMessagePanelPosition(): {scrollHeight: number; scrollTop: number} | null {
    const el = this.messagePanel?.nativeElement;
    if (!el) {
      return null;
    }
    return {
      scrollHeight: el.scrollHeight,
      scrollTop: el.scrollTop
    };
  }

  private isMessagePanelNearBottom(): boolean {
    const el = this.messagePanel?.nativeElement;
    if (!el) {
      return true;
    }
    return el.scrollHeight - (el.scrollTop + el.clientHeight) <= 120;
  }

  private openImageAt(index: number): void {
    const message = this.imageMessages[index];
    if (!message) {
      return;
    }

    this.imageViewerIndex = index;
    this.imageViewerLoading = true;
    this.imageViewerSrc = '';
    this.imageViewerAlt = this.messageAttachmentName(message.messageData);
    this.requestCheck();
    this.loadImageAt(index);
  }

  private loadImageAt(index: number): void {
    const message = this.imageMessages[index];
    if (!message) {
      this.imageViewerLoading = false;
      this.requestCheck();
      return;
    }

    const src = message.messageData;
    const image = new Image();
    image.onload = () => {
      this.imageViewerSrc = src;
      this.imageViewerLoading = false;
      this.preloadAdjacentImages(index);
      this.requestCheck();
    };
    image.onerror = () => {
      this.imageViewerLoading = false;
      this.toast.error('โหลดรูปไม่สำเร็จ');
      this.requestCheck();
    };
    image.src = src;
  }

  private preloadAdjacentImages(index: number): void {
    this.preloadImage(this.imageMessages[index - 1]?.messageData);
    this.preloadImage(this.imageMessages[index + 1]?.messageData);
  }

  private preloadImage(src?: string): void {
    if (!src) {
      return;
    }

    const image = new Image();
    image.src = src;
  }

  private sortContactsByRecentActivity(contacts: ChatContactView[]): ChatContactView[] {
    return [...contacts].sort((left, right) => {
      const rightTime = right.lastMessageAt ? new Date(right.lastMessageAt).getTime() : 0;
      const leftTime = left.lastMessageAt ? new Date(left.lastMessageAt).getTime() : 0;
      if (rightTime !== leftTime) {
        return rightTime - leftTime;
      }
      return right.contactId - left.contactId;
    });
  }
}
